import { defaultCellClassName } from '@frontend/components/list';
import { ToastUtil } from '@frontend/toast-utils';
import type { CellContext } from '@tanstack/react-table';
import { LuCopy } from 'react-icons/lu';
import { FormattedMessage, useIntl } from 'react-intl';

interface CopyableTrackingNumberProps {
    value: string | null | undefined;
}

const copySuccessMessage = (
    <FormattedMessage
        id='platform.components.copyable-tracking-number-cell.copy-success'
        description='Toast shown after copying a tracking number from a transaction list'
        defaultMessage='Tracking number copied to clipboard'
    />
);

export const CopyableTrackingNumber = (props: CopyableTrackingNumberProps) => {
    const intl = useIntl();
    const value = props.value?.trim();
    if (!value) {
        return <span>/</span>;
    }

    const copyLabel = intl.formatMessage({
        id: 'platform.components.copyable-tracking-number-cell.copy-aria',
        description: 'Accessible label for the copy tracking number button in transaction lists',
        defaultMessage: 'Copy tracking number'
    });

    return (
        <div className='flex min-w-0 items-center gap-2'>
            <span className='truncate'>{value}</span>
            <button
                type='button'
                aria-label={copyLabel}
                title={copyLabel}
                className='text-brand-muted hover:text-brand-accent focus-visible:outline-brand-accent shrink-0 cursor-pointer rounded p-0.5 opacity-0 transition-opacity group-hover:opacity-100 focus-visible:opacity-100 focus-visible:outline-2 focus-visible:outline-offset-2'
                onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    void navigator.clipboard.writeText(value).then(() => {
                        ToastUtil.success(copySuccessMessage);
                    });
                }}>
                <LuCopy className='size-4' />
            </button>
        </div>
    );
};

/** TanStack `customCells` renderer for transaction `tracking_number` columns. */
export const renderCopyableTrackingNumberCell = <T extends { tracking_number?: string | null }>(props: CellContext<T, unknown>) => (
    <td className={defaultCellClassName}>
        <CopyableTrackingNumber value={props.row.original.tracking_number} />
    </td>
);
